import {UseQueryResult} from '@tanstack/react-query';
import {useEffect, useMemo, useState} from 'react';

import {DataLoadingSwitch} from '../../../../../../../component/loading-switch/loading-switch';
import {Text} from '../../../../../../../component/text/text';
import {AdditionalInfo} from '../../../../../../../layout/additional-info/additional-info';
import {LegendBox} from '../../../../../../../layout/chart/legend-box/legend-box';
import {LegendWrapper} from '../../../../../../../layout/chart/legend-wrapper/legend-wrapper';
import {LineChart} from '../../../../../../../layout/chart/line-chart/line-chart';
import {LineDataSetType, LineDataType} from '../../../../../../../layout/chart/line-chart/line-chart-type';
import {PageCard} from '../../../../../../../layout/page-card/page-card';
import {Locale} from '../../../../../../../provider/locale/localization';
import {ReviewAspectsToneType} from '../../../../../../../service/reivew-analysis/review-aspects/review-aspects-type';
import {capitalizeFirstLetter} from '../../../../../../../util/string';
import {useReviewsAnalysisFilter, useReviewsAnalysisTooltipFormatter} from '../../../../reviews-analysis-hook';
import {getAspectsToneChartOptions, getAspectsToneColor} from '../aspects-helper';

import * as styles from './aspects-tone-dynamics-charts.scss';

type PropsType = {
    aspectsToneDynamics: UseQueryResult<ReviewAspectsToneType>;
};

export function AspectsToneDynamicsCharts({aspectsToneDynamics}: PropsType): JSX.Element {
    const {data, isLoading, isError} = aspectsToneDynamics;

    const isEmpty = useMemo(() => {
        return data?.aspectsData && data.aspectsData.length === 0;
    }, [data]);

    const {comparePeriodFilter} = useReviewsAnalysisFilter(false);

    const isCompareMode: boolean = useMemo(() => {
        return Boolean(comparePeriodFilter.startDate);
    }, [comparePeriodFilter.startDate]);

    const aspects = useMemo(() => {
        return (
            data?.aspectsData.map((aspectData) => {
                return aspectData.aspect;
            }) || []
        );
    }, [data?.aspectsData]);

    const [enabledDatasetsMap, setEnabledDatasetsMap] = useState<Record<string, boolean>>({});
    const [compareDatasetsVisible, setCompareDatasetsVisible] = useState<boolean>(isCompareMode);

    useEffect(() => {
        setEnabledDatasetsMap(
            aspects.reduce((accumulator, aspect) => {
                return {
                    ...accumulator,
                    [aspect]: true,
                };
            }, {} as Record<string, boolean>) || {}
        );
    }, [aspects, data]);

    useEffect(() => {
        setCompareDatasetsVisible(isCompareMode);
    }, [isCompareMode]);

    const mainDatasets: Array<LineDataSetType> = useMemo(() => {
        return (
            data?.aspectsData.flatMap((aspectData, index) => {
                const datasetColor = getAspectsToneColor(index);

                if (!enabledDatasetsMap[aspectData.aspect]) {
                    return [];
                }

                return [
                    {
                        label: aspectData.aspect,
                        values:
                            aspectData.currentPeriodData.length > 0
                                ? aspectData.currentPeriodData
                                : Array.from({length: data.labels.length}, () => 0),
                        color: datasetColor,
                        borderColor: datasetColor,
                    },
                ];
            }) || []
        );
    }, [data, enabledDatasetsMap]);

    const compareDatasets: Array<LineDataSetType> = useMemo(() => {
        if (!compareDatasetsVisible || !isCompareMode) {
            return [];
        }

        return (
            data?.aspectsData.flatMap((aspectData, index) => {
                const datasetColor = getAspectsToneColor(index);

                if (!enabledDatasetsMap[aspectData.aspect]) {
                    return [];
                }

                return [
                    {
                        label: aspectData.aspect,
                        values: aspectData.comparePeriodData?.length
                            ? aspectData.comparePeriodData
                            : Array.from({length: data.labels.length}, () => 0),
                        dashed: true,
                        color: datasetColor,
                        borderColor: datasetColor,
                    },
                ];
            }) || []
        );
    }, [compareDatasetsVisible, data, enabledDatasetsMap, isCompareMode]);

    const chartData: LineDataType = useMemo(() => {
        return {
            labels: data?.labels || [],
            datasets: [...mainDatasets, ...compareDatasets],
        };
    }, [compareDatasets, data?.labels, mainDatasets]);

    const getTooltipLabel = useReviewsAnalysisTooltipFormatter();

    return (
        <PageCard
            bodyClassName={styles.AspectsToneDynamics}
            title={
                <AdditionalInfo
                    leftNode={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE" />}
                    popoverClassName={styles.AspectsToneDynamics_additionalInfo}
                >
                    <Locale
                        stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE__ADDITIONAL_INFO"
                        valueMap={{
                            formula: (
                                <Text strong>
                                    <Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__ASPECTS_TONE__ADDITIONAL_INFO_FORMULA" />
                                </Text>
                            ),
                        }}
                    />
                </AdditionalInfo>
            }
        >
            <DataLoadingSwitch isEmpty={isEmpty} isError={isError} isLoading={isLoading}>
                <LegendWrapper>
                    {aspects.map((aspect, index) => {
                        return (
                            <LegendBox
                                checked={Boolean(enabledDatasetsMap[aspect])}
                                color={getAspectsToneColor(index)}
                                key={aspect}
                                label={capitalizeFirstLetter(aspect)}
                                onChange={() =>
                                    setEnabledDatasetsMap((previousState) => {
                                        return {
                                            ...previousState,
                                            [aspect]: !previousState[aspect],
                                        };
                                    })
                                }
                            />
                        );
                    })}
                    {isCompareMode && (
                        <LegendBox
                            checked={compareDatasetsVisible}
                            key="compare"
                            label={<Locale stringKey="REVIEWS_ANALYSIS__KEYWORDS_AND_TOPICS__COMPARE_PERIOD" />}
                            onChange={() => setCompareDatasetsVisible((previousState) => !previousState)}
                            stripes
                        />
                    )}
                </LegendWrapper>

                <LineChart
                    data={chartData}
                    options={getAspectsToneChartOptions({
                        data: {
                            labels: chartData.labels,
                            datasets: mainDatasets,
                        },
                        ...(compareDatasetsVisible
                            ? {
                                  compareData: {
                                      labels: chartData.labels,
                                      datasets: compareDatasets,
                                  },
                              }
                            : {}),
                        formatLabel: getTooltipLabel,
                    })}
                />
            </DataLoadingSwitch>
        </PageCard>
    );
}
